import React, { useState, useEffect } from 'react';
import { Card, Spinner, Alert, Button, Form, Row, Col, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { driversAPI } from '../../api/apiClient';
import { useTranslation } from 'react-i18next';

export default function SelectDriver() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch drivers with search
  const fetchDrivers = async (search = searchTerm) => {
    try {
      setLoading(true);
      setError('');
      const response = await driversAPI.index({ search });
      
      if (response.success) {
        const driversData = response.data.data || response.data.data?.data || [];
        
        const validDrivers = driversData.filter(driver => 
          driver && (driver.firstname || driver.email || driver.phone)
        );
        
        setDrivers(validDrivers);
      } else {
        setError(response.message || t('failed_to_fetch_Drivers'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_drivers'));
      console.error('Error fetching drivers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Debounced Search Effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchDrivers(searchTerm);
      }
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  useEffect(() => {
    fetchDrivers();
  }, []);

  // دالة للحصول على الحرف الأول من الاسم بشكل آمن
  const getInitial = (driver) => {
    if (driver.firstname) {
      return driver.firstname.charAt(0).toUpperCase();
    } else if (driver.email) {
      return driver.email.charAt(0).toUpperCase();
    } else if (driver.phone) {
      return driver.phone.charAt(0);
    }
    return '?';
  };

  // دالة للحصول على الاسم المعروض بشكل آمن
  const getDisplayName = (driver) => {
    if (driver.firstname) {
      return driver.firstname;
    } else if (driver.email) {
      return driver.email;
    } else if (driver.phone) {
      return driver.phone;
    }
    return t('unknown_driver');
  };

  // دالة للحصول على التفاصيل المعروضة بشكل آمن
  const getDisplayDetails = (driver) => {
    const details = [];
    if (driver.email) details.push(driver.email);
    if (driver.phone) details.push(driver.phone);
    return details.join(' • ') || t('no_contact_info');
  };

  const startChat = (driver) => {
    navigate(`/admin/driver-list/chat-driver/${driver.id}`, {
      state: { newConversation: true }
    });
  };

  if (loading) {
    return (
      <div className="container py-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
        <h4 className="fw-bold mb-0">👥 {t('select_driver')}</h4>
        <Button className='add-btn btn btn-primary' onClick={() => navigate('/admin/driver-list')}>
          {t('back_to_conversations')}
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Search Bar */}
      <Form.Group className="mb-4">
        <Form.Control
          type="text"
          placeholder={t('search_customers_placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="rounded-pill"
        />
        <Form.Text className="text-muted">
          {t('search_by_name_email_phone')}
        </Form.Text>
      </Form.Group>

      {drivers.length === 0 ? (
        <Alert variant="info">
          {searchTerm ? t('no_drivers_found') : t('no_drivers_available')}
        </Alert>
      ) : (
        <Row>
          {drivers.map(driver => (
            <Col md={6} lg={4} key={driver.id} className="mb-3">
              <Card className="h-100 shadow-sm driver-card">
                <Card.Body className="text-center">
                  <div className="driver-avatar mb-3">
                    <div className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center"
                         style={{ width: '60px', height: '60px', fontSize: '24px' }}>
                      {getInitial(driver)}
                    </div>
                  </div>
                  
                  <h6 className="fw-bold mb-1">{getDisplayName(driver)}</h6>
                  <p className="text-muted small mb-2">{getDisplayDetails(driver)}</p>
                  
                  {driver.role_name && (
                    <Badge bg="secondary" className="mb-2">
                      {driver.role_name}
                    </Badge>
                  )}
                  
                  <div className="mt-3">
                    <Button
                      variant="success"
                      onClick={() => startChat(driver)}
                      className="rounded-pill px-4"
                    >
                      {t('start_chat')}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <style>{`
        .driver-card {
          transition: transform 0.2s;
          border: 1px solid #e9ecef;
        }
        
        .driver-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          border-color: #28a745;
        }
      `}</style>
    </div>
  );
}